import React, { useState, useEffect, useCallback } from 'react';
import {
    Package, Plus, Upload, Search, AlertTriangle, Check, X,
    CalendarClock, TrendingDown, FileText, Pencil
} from 'lucide-react';
import Select from 'react-select';
import { magazzinoAPI } from '../services/magazzinoApi';
import { prestazioniAPI } from '../services/api';
import { tracciabilitaAPI } from '../services/tracciabilitaApi';
/* La schermata della distinta esiste gia' e la si riusa tale e quale:
   riscriverla qui vorrebbe dire due punti dove correggere la stessa
   regola, e prima o poi se ne correggerebbe uno solo. */
import DistintaPrestazione from '../components/prestazioni/DistintaPrestazione';
import ContiMedici from '../components/magazzino/ContiMedici';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import '../styles/Magazzino.css';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import { notificaAvviso } from '../utils/notifiche';
import { useAuthStore } from '../store/useStore';

/* Le sotto-viste stanno qui e non dentro il JSX: l'elenco e' lo stesso
   che decide quali linguette esistono e quale riquadro si disegna, e
   tenerlo in un posto solo evita la linguetta senza contenuto. */
const SCHEDE = [
    { chiave: 'giacenze',  etichetta: 'Giacenze' },
    { chiave: 'scarico',   etichetta: 'Da scaricare' },
    { chiave: 'prodotti',  etichetta: 'Prodotti' },
    { chiave: 'scadenze',  etichetta: 'Scadenze' },
    { chiave: 'movimenti', etichetta: 'Movimenti' },
    /* «Senza distinta» sta qui e non fra le Prestazioni: la domanda e'
       di magazzino - che cosa consuma questa prestazione - e chi la fa
       sta guardando le giacenze, non il listino. */
    { chiave: 'senza-distinta', etichetta: 'Senza distinta' },
    /* Di chi sono le unita' che stanno in frigo. Sta qui e non in
       Amministrazione perche' chi assegna un carico lo assegna subito
       dopo averlo caricato, ed e' questa la schermata che ha aperta. */
    { chiave: 'conti',     etichetta: 'Conti medici' },
];

/**
 * Magazzino — giacenze, lotti, movimenti, carico da fattura.
 *
 * Il carico da XML avviene in due tempi: il file viene letto e
 * proposto, poi l'operatore conferma riga per riga aggiungendo lotto
 * e scadenza, che nel tracciato della fattura elettronica non ci sono.
 */

const euro = (v) =>
    (parseFloat(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? '0' : n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
};

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

/* Le nature IVA di una riga a zero (fattura del fornitore, migration 271). */
const NATURE_IVA = ['N1', 'N2.1', 'N2.2', 'N3.1', 'N3.2', 'N3.3', 'N3.4', 'N3.5', 'N3.6', 'N4', 'N5',
                    'N6.1', 'N6.2', 'N6.3', 'N6.4', 'N6.5', 'N6.6', 'N6.7', 'N6.8', 'N6.9', 'N7'];

const Magazzino = () => {
    const [vista, setVista] = useState('giacenze');
    /* Il lotto da cui si arriva ai conti. Sta qui e non dentro
       ContiMedici perche' a sceglierlo e' la riga delle Scadenze,
       che vive in questa pagina. */
    const [lottoPerConti, setLottoPerConti] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    const [riepilogo, setRiepilogo] = useState({});
    const [giacenze, setGiacenze] = useState([]);
    const [scadenze, setScadenze] = useState([]);
    const [movimenti, setMovimenti] = useState([]);
    const [codaScarico, setCodaScarico] = useState([]);
    const [senzaDistinta, setSenzaDistinta] = useState([]);
    /* Il costo d'acquisto (blocco 3, migration 247): i lotti ancora senza
       costo, gli articoli che stanno in una distinta (per loro il carico
       lo pretende) e se il costo e' IVA compresa o esclusa. */
    const [costi, setCosti] = useState({ lotti_senza_costo: [], prodotti_in_distinta: [], iva: 'da_decidere' });
    const [costiScritti, setCostiScritti] = useState({});   // lotto id -> testo
    const [vediSenzaCosto, setVediSenzaCosto] = useState(false);
    const [soloIniettabili, setSoloIniettabili] = useState(true);
    const [cercaPrestazione, setCercaPrestazione] = useState('');
    const [distintaDi, setDistintaDi] = useState(null);
    const [lottiPerArticolo, setLottiPerArticolo] = useState({});
    const [formRettifica, setFormRettifica] = useState(null);
    /* Il costo di un lotto non piu' disponibile lo corregge l'amministrazione
       (blocco 4, migration 251): lo decide il server, qui si mostra. */
    const amministrazione = useAuthStore((s) => s.user)?.ruolo === 'admin';
    const [ricerca, setRicerca] = useState('');
    /* I prodotti a giacenza zero sono quattrocento su quattrocentonove:
       si nascondono di suo, e questa spunta li rimette. Non e' un filtro
       fra i tanti - e' la differenza fra un elenco che si guarda e uno
       che si scorre. */
    const [conZero, setConZero] = useState(false);

    const [pannello, setPannello] = useState(null);   // 'carico' | 'xml' | 'prodotto'
    const [prodotti, setProdotti] = useState([]);
    const [formProdotto, setFormProdotto] = useState(null);
    /* Le prestazioni collegate al prodotto nel modulo: e' la distinta
       vista dall'altra parte, stessa tabella. `null` finche' non sono
       state lette, cosi' un salvataggio prima della lettura non le
       azzera. */
    const [prestazioniProdotto, setPrestazioniProdotto] = useState(null);
    const [catalogoPrestazioni, setCatalogoPrestazioni] = useState([]);
    const [formCarico, setFormCarico] = useState({
        prodotto_id: '', quantita: '', codice_lotto: '', scadenza: '',
        imponibile_unitario: '', aliquota_iva: '', natura_iva: '', fornitore: '', documento: '',
    });

    // Carico da fattura
    const [xmlLetto, setXmlLetto] = useState(null);
    const [letturaInCorso, setLetturaInCorso] = useState(false);
    const [lettureInAttesa, setLettureInAttesa] = useState([]);
    const [tipoDocumento, setTipoDocumento] = useState('fattura');   // per un PDF o una foto caricati da qui
    /* Quando la somma di controllo non torna, il carico si sblocca solo
       con una spunta esplicita: «ho guardato». */
    const [controllato, setControllato] = useState(false);
    // Vero mentre un file sta sopra l'area di rilascio
    const [sopra, setSopra] = useState(false);
    const [righeXml, setRigheXml] = useState([]);
    const [inCorso, setInCorso] = useState(false);
    // Lotti scritti che non risultano a magazzino: { elenco, messaggio }
    const [daConfermare, setDaConfermare] = useState(null);

    /* «Vuoi uscire senza salvare» sulle tre finestre che compilano:
       rettifica, prodotto, carico manuale. Un confronto solo con
       l'istantanea presa all'apertura (versioneModuli sale li'), e le
       chiusure da sfondo, X e Annulla passano da chiudi*(). */
    const [versioneModuli, setVersioneModuli] = useState(0);
    const sporco = useSporco({
        rettifica: formRettifica ? { lotto_id: formRettifica.lotto_id, differenza: formRettifica.differenza,
                                     motivo: formRettifica.motivo, tipo: formRettifica.tipo,
                                     costo: formRettifica.costo } : null,
        prodotto: pannello === 'prodotto' ? formProdotto : null,
        carico: pannello === 'carico' ? formCarico : null,
    }, versioneModuli);
    const chiedi = useUscitaProtetta(sporco);
    const chiudiRettifica = () => { if (chiedi()) setFormRettifica(null); };
    const chiudiProdotto = () => { if (chiedi()) { setPannello(null); setFormProdotto(null); } };
    const chiudiCarico = () => { if (chiedi()) setPannello(null); };

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const [ri, gi, sc, mo, co, lo, sd] = await Promise.all([
                magazzinoAPI.riepilogo(90),
                magazzinoAPI.giacenze({ q: ricerca || undefined, conZero: conZero || undefined }),
                magazzinoAPI.scadenze(90),
                magazzinoAPI.movimenti({}),
                /* Un anno, non trenta giorni. Con la finestra a trenta una
                   somministrazione non registrata SPARIVA dalla coda al
                   trentunesimo giorno: nessuno l'avrebbe piu' vista, e per
                   un dispositivo tracciato quel lotto non si recupera piu'.
                   Il 3 settembre 2026 era gia' successo a una riga del 28
                   luglio. Le vecchie si segnalano, non si nascondono. */
                magazzinoAPI.codaScarico(365),
                magazzinoAPI.lotti({ stato: 'disponibile' }),
                magazzinoAPI.senzaDistinta(),
            ]);
            /* A parte e senza fermare il resto: se non risponde, il
               magazzino si apre lo stesso. */
            magazzinoAPI.costi().then((r) => setCosti(r.data.data)).catch(() => {});
            setRiepilogo(ri.data.data);
            setGiacenze(gi.data.data.giacenze || []);
            setScadenze(sc.data.data.lotti || []);
            setMovimenti(mo.data.data.movimenti || []);
            setSenzaDistinta(sd.data.data.prestazioni || []);

            // I lotti disponibili si raggruppano per articolo: servono
            // alla scelta riga per riga nella coda di scarico.
            const perArticolo = {};
            (lo.data.data.lotti || []).forEach((l) => {
                if (parseFloat(l.quantita_residua) <= 0) return;
                (perArticolo[l.prodotto_id] = perArticolo[l.prodotto_id] || []).push(l);
            });
            setLottiPerArticolo(perArticolo);

            setCodaScarico((co.data.data.coda || []).map((r) => {
                const disponibili = perArticolo[r.prodotto_id] || [];
                return {
                    ...r,
                    // Predefinito: scadenza piu' vicina. Sui prodotti
                    // tracciati va comunque confermato con l'etichetta.
                    lotto_id: disponibili.length ? disponibili[0].id : '',
                    /* Il codice si scrive leggendolo dal tagliando che il
                       medico ha consegnato. Si propone quello del lotto in
                       scadenza, ma resta da confermare: proporre e' comodo,
                       dare per buono no - se la merce e' arrivata e non e'
                       ancora stata caricata, il numero giusto in tendina
                       non c'e' proprio. */
                    codice_lotto: r.tracciabilita_rigorosa && disponibili.length
                        ? (disponibili[0].codice_lotto || '') : '',
                    quantita_scarico: r.quantita,
                    /* Non si spuntano da sole: i tracciati, perche' il lotto
                       va letto dall'etichetta; e le RETROATTIVE, cioe' quelle
                       dove la prestazione e' stata eseguita prima che il
                       prodotto entrasse nella distinta. Quelle il lotto
                       proposto oggi potrebbe non averlo nemmeno visto: la
                       merce non e' uscita per quella procedura, o e' uscita
                       da un altro lotto, e nessuno lo sa piu'. Spuntarle da
                       sole vorrebbe dire scaricare merce vera con un
                       «Scarica selezionate» dato per scontato. */
                    includi: !r.tracciabilita_rigorosa && !r.retroattiva && disponibili.length > 0,
                };
            }));
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare il magazzino'));
        } finally {
            setCaricamento(false);
        }
    }, [ricerca, conZero]);

    useEffect(() => { carica(); }, [carica]);

    useEffect(() => {
        magazzinoAPI.prodotti({})
            .then((r) => setProdotti(r.data.data.prodotti || []))
            .catch((e) => console.error('Prodotti:', e));
    }, []);

    /* ---------- carico manuale ---------- */

    const salvaCarico = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            /* Imponibile e aliquota della fattura del fornitore (migration
               271): il costo lo calcola il server, secondo il parametro. */
            await magazzinoAPI.carico({
                ...formCarico,
                quantita: parseFloat(formCarico.quantita),
                imponibile_unitario: formCarico.imponibile_unitario === '' ? null
                    : parseFloat(String(formCarico.imponibile_unitario).replace(',', '.')),
                aliquota_iva: formCarico.aliquota_iva === '' ? null
                    : parseFloat(String(formCarico.aliquota_iva).replace(',', '.')),
                natura_iva: String(formCarico.aliquota_iva) === '0' ? formCarico.natura_iva || null : null,
            });
            setFormCarico({ prodotto_id: '', quantita: '', codice_lotto: '', scadenza: '',
                            imponibile_unitario: '', aliquota_iva: '', natura_iva: '', fornitore: '', documento: '' });
            setPannello(null);
            setAvviso('Merce caricata');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel carico'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- rettifica di giacenza ---------- */

    /* Il costo nel campo e' quello del lotto scelto: cambiando lotto si
       riparte dal suo, altrimenti si salverebbe sul lotto nuovo il costo
       letto sul vecchio. */
    const costoDelLotto = (lotti, id) => {
        const l = lotti.find((x) => String(x.id) === String(id));
        return l && l.costo_unitario != null ? String(parseFloat(l.costo_unitario)) : '';
    };
    /* Imponibile, aliquota e natura del lotto scelto (migration 271): i
       lotti di prima non li hanno, e il campo resta vuoto da riempire. */
    const ivaDelLotto = (lotti, id) => {
        const l = lotti.find((x) => String(x.id) === String(id)) || {};
        return {
            imponibile: l.imponibile_unitario != null ? String(parseFloat(l.imponibile_unitario)) : '',
            aliquota: l.aliquota_iva != null ? String(parseFloat(l.aliquota_iva)) : '',
            natura: l.natura_iva || '',
        };
    };

    const apriRettifica = async (g) => {
        let lotti = lottiPerArticolo[g.prodotto_id] || [];
        /* L'amministrazione vede anche i lotti esauriti, scaduti e smaltiti:
           il loro costo e' ancora quello di compensi maturati, e va potuto
           correggere. I disponibili restano in cima. */
        if (amministrazione) {
            try {
                const r = await magazzinoAPI.lotti({ prodotto_id: g.prodotto_id });
                const tutti = r.data.data.lotti || [];
                lotti = [...tutti.filter((l) => l.stato === 'disponibile'),
                         ...tutti.filter((l) => l.stato !== 'disponibile')];
            } catch (e) { /* restano i disponibili */ }
        }
        const primo = lotti.length ? lotti[0].id : '';
        setFormRettifica({
            articolo: g,
            lotti,
            lotto_id: primo,
            differenza: '',
            motivo: '',
            motivoCosto: '',
            tipo: 'rettifica',
            costo: costoDelLotto(lotti, primo),
            ...ivaDelLotto(lotti, primo),
        });
        setVersioneModuli((v) => v + 1);
    };

    const salvaRettifica = async () => {
        try {
            setErrore(null);
            const resoFornitore = formRettifica.tipo === 'reso_fornitore';
            if (resoFornitore && !(parseFloat(formRettifica.differenza) < 0)) {
                setErrore('Un reso al fornitore toglie merce: la differenza deve essere negativa.');
                return;
            }
            setInCorso(true);
            await magazzinoAPI.rettifica({
                lotto_id: formRettifica.lotto_id,
                quantita: parseFloat(formRettifica.differenza),
                /* Il motivo dice che e' un reso al fornitore, cosi' nello
                   storico si distingue da una conta di inventario. */
                motivo: resoFornitore && !/reso/i.test(formRettifica.motivo)
                    ? `Reso al fornitore — ${formRettifica.motivo}` : formRettifica.motivo,
                tipo: resoFornitore ? 'rettifica' : formRettifica.tipo,
            });
            setFormRettifica(null);
            setAvviso('Rettifica registrata: resta nello storico dei movimenti.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella rettifica'));
        } finally {
            setInCorso(false);
        }
    };

    /* Il costo si salva da solo, con il suo pulsante: non e' un movimento
       e non vuole un motivo, e chi corregge un prezzo non deve inventarsi
       una differenza di giacenza per poterlo fare. */
    const salvaCostoLotto = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const r = await magazzinoAPI.costoLotto(formRettifica.lotto_id, {
                imponibile_unitario: String(formRettifica.imponibile).replace(',', '.'),
                aliquota_iva: String(formRettifica.aliquota).replace(',', '.'),
                natura_iva: String(formRettifica.aliquota) === '0' ? formRettifica.natura : null,
            }, formRettifica.motivoCosto || undefined);
            const nuovo = r.data.data.lotto;
            const lotti = formRettifica.lotti.map((l) =>
                String(l.id) === String(formRettifica.lotto_id) ? { ...l, ...nuovo } : l);
            setFormRettifica({ ...formRettifica, lotti,
                               costo: nuovo.costo_unitario != null ? String(parseFloat(nuovo.costo_unitario)) : '',
                               ...ivaDelLotto(lotti, formRettifica.lotto_id), motivoCosto: '' });
            setVersioneModuli((v) => v + 1);
            setAvviso(r.data.data.cambiato
                ? 'Costo del lotto salvato: il valore di prima resta nel registro modifiche.'
                : 'Il costo era già questo: niente da salvare.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio del costo'));
        } finally {
            setInCorso(false);
        }
    };

    /* L'etichetta del costo al carico: l'unita' dell'articolo, l'IVA quando
       la contabilita' l'ha decisa, e l'asterisco per un articolo che sta in
       una distinta, dove il carico senza costo non passa (migration 247). */
    /* Al carico si scrivono imponibile e aliquota della fattura del
       fornitore (migration 271); che cosa vale come costo lo dice il
       parametro della clinica, e il conto lo fa il server. */
    const regolaCosto = () => (costi.iva === 'esclusa' ? 'come costo vale l’imponibile (IVA esclusa)'
        : costi.iva === 'indetraibile'
            ? (costi.iva_percentuale !== '' ? `come costo vale l’imponibile più il ${costi.iva_percentuale}% dell’IVA (indetraibile)`
                : 'come costo vale l’imponibile più l’IVA indetraibile: manca la percentuale, il costo resta da completare')
            : 'come costo vale l’imponibile più l’IVA (IVA compresa)');
    const etichettaCosto = (prodottoId) => {
        const p = prodotti.find((x) => String(x.prodotto_id) === String(prodottoId));
        const unita = p?.unita_misura || 'unità';
        const obbligo = costi.prodotti_in_distinta.some((id) => String(id) === String(prodottoId)) ? ' *' : '';
        return `Imponibile unitario (per ${unita})${obbligo}`;
    };
    const salvaCostoRiga = async (id) => {
        try {
            setErrore(null);
            setInCorso(true);
            const c = costiScritti[id] || {};
            await magazzinoAPI.costoLotto(id, {
                imponibile_unitario: String(c.imponibile || '').replace(',', '.'),
                aliquota_iva: String(c.aliquota || '').replace(',', '.'),
                natura_iva: String(c.aliquota) === '0' ? c.natura || null : null,
            });
            setCostiScritti((c) => { const n = { ...c }; delete n[id]; return n; });
            setAvviso('Costo del lotto salvato.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio del costo'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- scarico dalla coda ---------- */

    /* Quanto e' vecchia una riga in attesa: piu' passa, meno probabile
       che il tagliando esista ancora. */
    const giorniDa = (data) => {
        const d = new Date(data);
        return isNaN(d) ? 0 : Math.floor((Date.now() - d.getTime()) / 86400000);
    };

    const aggiornaCoda = (i, campo, valore) =>
        setCodaScarico((prev) => prev.map((r, k) => (k === i ? { ...r, [campo]: valore } : r)));

    const pronte = () => codaScarico.filter((x) => x.includi
        && (x.tracciabilita_rigorosa ? String(x.codice_lotto).trim() : x.lotto_id));

    /**
     * Le due strade non sono un doppione.
     *
     * I prodotti tracciati passano per /tracciabilita/registra, che accetta
     * un codice letto dall'etichetta e sa cosa fare quando quel numero non
     * risulta a magazzino: chiede conferma e lo crea segnato da
     * regolarizzare. Gli altri passano per lo scarico multiplo, che sceglie
     * il lotto per identificativo. Unire le due cose vorrebbe dire o
     * rinunciare alla conferma sui tracciati, o chiederla su prodotti che
     * non hanno un'etichetta da leggere.
     */
    const scaricaSelezionate = async (confermaLotti = false) => {
        try {
            setErrore(null);
            setInCorso(true);
            const scelte = pronte();
            const tracciate = scelte.filter((x) => x.tracciabilita_rigorosa);
            const semplici = scelte.filter((x) => !x.tracciabilita_rigorosa);
            const messaggi = [];

            if (tracciate.length) {
                try {
                    const t = await tracciabilitaAPI.registra(
                        tracciate.map((x) => ({
                            visita_prestazione_id: x.visita_prestazione_id,
                            prodotto_id: x.prodotto_id,
                            codice_lotto: String(x.codice_lotto).trim(),
                            /* NIENTE «|| 1»: uno zero scritto per dire «non usato»
                               scaricava un'unita' vera. Il server rifiuta, e qui
                               non si ripiega piu'. Vedi backend/utils/quantita.js. */
                            quantita: String(x.quantita_scarico ?? '').trim(),
                            paziente_id: x.paziente_id,
                        })),
                        confermaLotti
                    );
                    messaggi.push(t.data.message);
                } catch (e) {
                    const d = e?.response?.data;
                    // 409: uno o piu' lotti non risultano. Non e' un errore,
                    // e' una domanda: si mostra quali e si lascia decidere.
                    if (d?.serve_conferma) {
                        setDaConfermare({ elenco: d.data.sconosciuti, messaggio: d.message });
                        return;
                    }
                    throw e;
                }
            }

            if (semplici.length) {
                const r = await magazzinoAPI.scaricoMultiplo(
                    semplici.map((x) => ({
                        visita_prestazione_id: x.visita_prestazione_id,
                        prodotto_id: x.prodotto_id,
                        /* NIENTE «|| 1»: uno zero scritto per dire «non usato»
                               scaricava un'unita' vera. Il server rifiuta, e qui
                               non si ripiega piu'. Vedi backend/utils/quantita.js. */
                            quantita: String(x.quantita_scarico ?? '').trim(),
                        lotto_id: x.lotto_id,
                        paziente_id: x.paziente_id,
                    }))
                );
                messaggi.push(r.data.message);
            }

            setDaConfermare(null);
            setAvviso(messaggi.join(' ') || 'Niente da scaricare.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nello scarico'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- carico da fattura ---------- */

    /* Una lettura - da XML o da PDF - diventa la schermata di controllo.
       E' una funzione sola perche' la schermata e' una sola: le righe
       arrivano nella stessa forma da tutte e due le strade. */
    const accogliLettura = (d, messaggioServer) => {
            setControllato(false);
            setXmlLetto(d);
            setRigheXml(d.righe.map((riga) => ({
                ...riga,
/* Ordine: memorizzato, poi assistenza, poi somiglianza — MA la
                   somiglianza vale solo se il server l'ha ritenuta
                   sufficiente.

                   Fino al 17/09/2026 qui si prendeva `proposte[0]` sempre,
                   qualunque fosse il punteggio. Il server calcola una
                   soglia (0,6) e sotto quella NON propone: quel controllo
                   veniva buttato via qui, e la tendina si presentava gia'
                   impostata su un prodotto che il server aveva scartato.

                   Visto dal vero su una fattura alimentare: «Keto Min plus
                   360g» risultava preselezionato su «Omega Formula Guna 30
                   cp.», somiglianza testuale ZERO, punteggio 0,25 - tutto
                   dal numero «30», che compare in «30 pz» e in «30 cp.».
                   Premendo Carica, quella merce sarebbe entrata sul
                   prodotto sbagliato, e l'errore si scopre a inventario.

                   I candidati restano nella tendina: chi carica li vede e
                   sceglie. Quello che non si fa piu' e' sceglierne uno al
                   posto suo. */
                scelta: riga.abbinamento
                    ? riga.abbinamento.prodotto_id
                    : riga.assistenza
                        ? riga.assistenza.prodotto_id
                        : (riga.origine_abbinamento === 'proposta' && riga.proposte[0]
                            ? riga.proposte[0].id
                            : ''),
                fattore: riga.abbinamento ? riga.abbinamento.fattore_conversione : 1,
                /* Da una BOLLA lotto e scadenza arrivano letti: si
                   precompilano e si segnano da verificare sulla confezione.
                   Da una fattura o dall'XML non ci sono mai. */
                codice_lotto: riga.lotto_letto || '',
                scadenza: riga.scadenza_letta || '',
                lotto_letto: !!(riga.lotto_letto || riga.scadenza_letta),
                includi: true,
            })));
            if (d.gia_importato || d.metodo) setAvviso(messaggioServer);
    };

    /* Il PDF non torna subito: il server lo mette in coda e risponde
       «ho cominciato». Si guarda ogni due secondi finche' la lettura
       non e' finita, bene o male; oltre i tre minuti si smette di
       guardare e lo si dice. */
    const attendiLettura = async (id) => {
        const FINO_A = Date.now() + 3 * 60 * 1000;
        while (Date.now() < FINO_A) {
            await new Promise((r) => setTimeout(r, 2000));
            const r = await magazzinoAPI.statoImportaPdf(id);
            const d = r.data.data;
            if (d.stato === 'letta') return { d, messaggio: r.data.message };
            if (d.stato === 'fallita') throw new Error(r.data.message);
        }
        throw new Error('La lettura sta ancora andando avanti: riprova fra un minuto '
                      + 'ricaricando lo stesso file, verrà riconosciuto.');
    };

    /* Una lettura fatta dal telefono si apre da qui: stessa attesa,
       stessa schermata di controllo. Chi conferma e' chi e' collegato
       adesso, non chi ha scattato. */
    const apriLettura = async (id) => {
        try {
            setErrore(null); setInCorso(true); setLetturaInCorso(true);
            const { d, messaggio: m } = await attendiLettura(id);
            accogliLettura(d, m);
        } catch (e) {
            setErrore(e.response ? messaggio(e, 'Errore nella lettura') : e.message);
        } finally {
            setInCorso(false); setLetturaInCorso(false);
        }
    };

    const caricaLettureInAttesa = async () => {
        try {
            const r = await magazzinoAPI.lettureInAttesa();
            setLettureInAttesa(r.data.data.letture || []);
        } catch (e) { /* l'elenco resta vuoto */ }
    };

    const leggiFile = async (file) => {
        if (!file) return;
        try {
            setErrore(null);
            setInCorso(true);
            if (/\.(xml|p7m)$/i.test(file.name)) {
                const contenuto = await file.text();
                const r = await magazzinoAPI.importaXml(contenuto, file.name);
                accogliLettura(r.data.data, r.data.message);
            } else {
                setLetturaInCorso(true);
                const r = await magazzinoAPI.importaPdf(file, tipoDocumento);
                const { d, messaggio: m } = await attendiLettura(r.data.data.lettura_id);
                accogliLettura(d, m);
            }
        } catch (e) {
            setErrore(e.response ? messaggio(e, 'Errore nella lettura della fattura') : e.message);
        } finally {
            setInCorso(false);
            setLetturaInCorso(false);
        }
    };

    const confermaXml = async () => {
        try {
            setErrore(null);
            /* Da un PDF la quantita' e' letta, e puo' mancare: una riga
               senza quantita' non si carica «a uno» in silenzio, si
               ferma qui e lo dice. */
            /* La somma di controllo che non torna - o che non si e'
               potuta fare - ferma il carico finche' qualcuno non spunta
               di aver guardato. Non e' un avviso: e' un blocco. */
            const q = xmlLetto.quadratura;
            const righeCheNonTornano = righeXml.filter((x) => x.includi && x.incoerente).length;
            if (((q && !q.torna) || righeCheNonTornano > 0) && !controllato) {
                setErrore((righeCheNonTornano > 0 && q && q.torna
                    ? `${righeCheNonTornano === 1 ? 'Una riga non torna' : righeCheNonTornano + ' righe non tornano'} (quantità × prezzo ≠ importo)`
                    : 'La somma di controllo non torna')
                    + ': controlla le righe e spunta «Ho controllato le righe» prima di caricare.');
                return;
            }
            const senzaQuantita = righeXml.filter((x) => x.includi && x.scelta && !(parseFloat(x.quantita) > 0));
            if (senzaQuantita.length) {
                setErrore(`${senzaQuantita.length === 1 ? 'Una riga non ha' : senzaQuantita.length + ' righe non hanno'} `
                        + 'la quantità: scrivila leggendo la fattura, oppure escludi la riga.');
                return;
            }
            setInCorso(true);
            const r = await magazzinoAPI.confermaCarico({
                fornitore: xmlLetto.fornitore,
                documento: xmlLetto.documento,
                righe: righeXml.filter((x) => x.includi && x.scelta).map((x) => ({
                    prodotto_id: x.scelta,
                    descrizione: x.descrizione,
                    codice_principale: x.codice_principale,
                    quantita: x.quantita,
                    fattore_conversione: parseFloat(x.fattore) || 1,
                    costo_effettivo: x.costo_effettivo,
                    aliquota: x.aliquota,
                    natura: x.natura || null,
                    codice_lotto: x.codice_lotto || null,
                    scadenza: x.scadenza || null,
                })),
            });
            setAvviso(r.data.message);
            setXmlLetto(null);
            setRigheXml([]);
            setPannello(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel carico'));
        } finally {
            setInCorso(false);
        }
    };

    const aggiornaRiga = (i, campo, valore) =>
        setRigheXml((prev) => prev.map((r, k) => (k === i ? { ...r, [campo]: valore } : r)));

    /* ---------- prodotti ---------- */

    const apriModuloProdotto = async (f) => {
        setFormProdotto(f);
        setPrestazioniProdotto(null);
        setPannello('prodotto');
        setVersioneModuli((v) => v + 1);
        try {
            const [cat, coll] = await Promise.all([
                catalogoPrestazioni.length
                    ? Promise.resolve(null)
                    : prestazioniAPI.getAll(true),
                f.id ? magazzinoAPI.prestazioniProdotto(f.id) : Promise.resolve(null),
            ]);
            if (cat) setCatalogoPrestazioni(cat.data.data.prestazioni || []);
            setPrestazioniProdotto((coll?.data.data.prestazioni || []).map((x) => ({
                prestazione_id: x.prestazione_id, nome: x.nome, quantita: String(x.quantita),
            })));
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a leggere le prestazioni collegate'));
        }
    };

    const salvaProdotto = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const f = formProdotto;
            let id = f.id;
            if (id) await magazzinoAPI.aggiornaProdotto(id, f);
            else id = (await magazzinoAPI.creaProdotto(f)).data.data.prodotto.id;
            /* I collegamenti si scrivono solo se sono stati letti: se
               la lettura e' fallita, `null` non tocca niente. */
            if (prestazioniProdotto) {
                await magazzinoAPI.impostaPrestazioniProdotto(id, prestazioniProdotto.map((x) => ({
                    prestazione_id: x.prestazione_id, quantita: x.quantita,
                })));
            }
            setFormProdotto(null);
            setPannello(null);
            const r = await magazzinoAPI.prodotti({});
            setProdotti(r.data.data.prodotti || []);
            await carica();
            setAvviso(f.id ? 'Prodotto aggiornato.' : 'Prodotto creato.');
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio del prodotto'));
        } finally {
            setInCorso(false);
        }
    };

    const disattiva = async (p) => {
        /* Disattiva, non cancella: i movimenti alle spalle sono la
           tracciabilita' di chi ha ricevuto cosa, e quella non si butta. */
        if (!window.confirm(`Disattivare "${p.nome}"? Resta nello storico ma non si potrà più caricare.`)) return;
        try {
            setErrore(null);
            await magazzinoAPI.disattivaProdotto(p.prodotto_id || p.id);
            const r = await magazzinoAPI.prodotti({});
            setProdotti(r.data.data.prodotti || []);
            await carica();
            setAvviso('Prodotto disattivato.');
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella disattivazione'));
        }
    };

    if (caricamento && !giacenze.length) {
        return <div className="mag-attesa">Caricamento…</div>;
    }

    return (
        <div className="mag-wrap">
            {/* Le aliquote che si trovano sulle fatture dei fornitori: un
                suggerimento, non un elenco chiuso (migration 271). */}
            <datalist id="mag-aliquote">
                {['22', '10', '5', '4', '0'].map((a) => <option key={a} value={a} />)}
            </datalist>
            <IntestazioneSezione
                titolo="Magazzino"
                icona={<Package size={22} aria-hidden="true" />}
                viste={SCHEDE.map((s) => ({
                    ...s,
                    /* Il conteggio compare solo se c'e' qualcosa da
                       scaricare: una pallina con dentro zero e' rumore. */
                    contatore: s.chiave === 'scarico' && codaScarico.length
                        ? codaScarico.length : undefined,
                }))}
                vista={vista}
                onVista={setVista}
                azione={
                    <div className="mag-azioni">
                        <button className="mag-btn" onClick={() => apriModuloProdotto({
                            codice: '', nome: '', unita_misura: 'pz',
                            scorta_minima: '', tracciabilita_rigorosa: false, note: '' })}>
                            <Plus size={16} /> Nuovo prodotto
                        </button>
                        <button className="mag-btn" onClick={() => { setPannello('carico'); setVersioneModuli((v) => v + 1); }}>
                            <Plus size={16} /> Carico manuale
                        </button>
                        <button className="mag-btn mag-btn--principale" onClick={() => { setPannello('xml'); caricaLettureInAttesa(); }}>
                            <Upload size={16} /> Da fattura fornitore
                        </button>
                    </div>
                }
            />

            {errore && (
                <div className="mag-errore">
                    <AlertTriangle size={16} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="mag-avviso">
                    <Check size={16} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}

            {/* ---------- riquadri di sintesi ---------- */}
            <div className="mag-sintesi">
                <button className="mag-tessera" onClick={() => setVista('scadenze')}>
                    <CalendarClock size={18} />
                    <b>{riepilogo.lotti_in_scadenza ?? 0}</b>
                    <span>lotti in scadenza (90 gg)</span>
                </button>
                <button className="mag-tessera" onClick={() => setVista('giacenze')}>
                    <TrendingDown size={18} />
                    <b>{riepilogo.sotto_scorta ?? 0}</b>
                    <span>sotto scorta minima</span>
                </button>
                <button className={`mag-tessera ${riepilogo.giacenze_negative ? 'allarme' : ''}`}
                        onClick={() => setVista('giacenze')}>
                    <AlertTriangle size={18} />
                    <b>{riepilogo.giacenze_negative ?? 0}</b>
                    <span>giacenze negative</span>
                </button>
                <div className="mag-tessera mag-tessera--valore">
                    <b>{euro(riepilogo.valore_magazzino)} €</b>
                    <span>valore a magazzino</span>
                </div>
            </div>

            {/* ---------- giacenze ---------- */}
            {vista === 'giacenze' && costi.lotti_senza_costo.length > 0 && (
                <div className="mag-riquadro mag-senza-costo">
                    <button type="button" className="mag-senza-costo-testa" onClick={() => setVediSenzaCosto((x) => !x)}
                            aria-expanded={vediSenzaCosto}>
                        <b>{costi.lotti_senza_costo.length}</b>{' '}
                        {costi.lotti_senza_costo.length === 1 ? 'lotto senza' : 'lotti senza'} costo d’acquisto o con l’IVA da verificare
                        <span className="mag-nota"> — da completare con imponibile e aliquota della fattura{vediSenzaCosto ? '' : ' (apri)'}</span>
                    </button>
                    {vediSenzaCosto && (
                        <div className="rm-scorre">
                            <table className="mag-tabella">
                                <thead><tr><th>Articolo</th><th>Lotto</th><th className="text-right">Residuo</th><th>Imponibile e IVA della fattura</th><th /></tr></thead>
                                <tbody>
                                    {costi.lotti_senza_costo.map((l) => (
                                        <tr key={l.id}>
                                            <td>{l.nome}{l.in_distinta ? <span className="mag-nota"> · in una distinta</span> : null}</td>
                                            <td>{l.codice_lotto || 'senza lotto'}{l.da_regolarizzare ? <span className="mag-nota"> · da regolarizzare</span> : null}
                                                {l.iva_da_verificare ? <span className="mag-nota"> · costo {euro(l.costo_unitario)} €, IVA da verificare</span> : null}</td>
                                            <td className="text-right">{num(l.quantita_residua)} {l.unita_misura}</td>
                                            <td>
                                                {/* Imponibile e aliquota (migration 271): il costo lo calcola il server. */}
                                                {['imponibile', 'aliquota'].map((campo) => (
                                                    <input key={campo} type="number" step={campo === 'imponibile' ? '0.0001' : '0.01'} min="0"
                                                           className="mag-costo-riga" list={campo === 'aliquota' ? 'mag-aliquote' : undefined}
                                                           disabled={!amministrazione && l.stato !== 'disponibile'}
                                                           title={!amministrazione && l.stato !== 'disponibile'
                                                               ? 'Lotto non più disponibile: il costo lo scrive l’amministrazione' : undefined}
                                                           placeholder={campo === 'imponibile' ? `imponibile per ${l.unita_misura || 'unità'}` : 'IVA %'}
                                                           value={(costiScritti[l.id] || {})[campo] || ''}
                                                           onChange={(e) => setCostiScritti({ ...costiScritti,
                                                               [l.id]: { ...(costiScritti[l.id] || {}), [campo]: e.target.value } })} />
                                                ))}
                                                {String((costiScritti[l.id] || {}).aliquota) === '0' && (
                                                    <select value={(costiScritti[l.id] || {}).natura || ''}
                                                            onChange={(e) => setCostiScritti({ ...costiScritti,
                                                                [l.id]: { ...(costiScritti[l.id] || {}), natura: e.target.value } })}>
                                                        <option value="">natura</option>
                                                        {NATURE_IVA.map((n) => <option key={n} value={n}>{n}</option>)}
                                                    </select>
                                                )}
                                            </td>
                                            <td>
                                                <button type="button" className="mag-btn"
                                                        disabled={inCorso || !(costiScritti[l.id] || {}).imponibile || !(costiScritti[l.id] || {}).aliquota}
                                                        onClick={() => salvaCostoRiga(l.id)}>Salva</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            {vista === 'giacenze' && (
                <div className="mag-riquadro">
                    <div className="mag-barra-giacenze">
                        <div className="mag-ricerca">
                            <Search size={16} />
                            <input placeholder="Cerca articolo…" value={ricerca}
                                   onChange={(e) => setRicerca(e.target.value)} />
                        </div>
                        <label className="mag-con-zero"
                               title="Di solito si vedono solo i prodotti che hai davvero">
                            <input type="checkbox" checked={conZero}
                                   onChange={(e) => setConZero(e.target.checked)} />
                            <span>Mostra anche quelli a zero</span>
                        </label>
                    </div>
                    {ricerca && !conZero && (
                        /* Cercando si vede tutto, anche chi sta a zero: va detto,
                           altrimenti chi trova un prodotto a zero crede che la
                           spunta non funzioni. */
                        <p className="mag-nota-ricerca">
                            Durante la ricerca compaiono anche i prodotti a giacenza zero.
                        </p>
                    )}
                    {giacenze.length === 0 ? (
                        <p className="mag-vuoto">
                            {ricerca
                                ? 'Nessun prodotto con questo nome.'
                                : conZero
                                    ? 'Nessun prodotto a magazzino. Si crea dalla scheda Prodotti, e si collega a una prestazione dal catalogo.'
                                    : 'Nessun prodotto con giacenza. Spunta «Mostra anche quelli a zero» per vedere tutto il magazzino.'}
                        </p>
                    ) : (
                        <table className="mag-tabella rm-scorre">
                            <thead>
                                <tr>
                                    <th>Articolo</th>
                                    <th className="num">Giacenza</th>
                                    <th className="num">Lotti</th>
                                    <th>Prima scadenza</th>
                                    <th className="num">Valore</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {giacenze.map((g) => {
                                    const sotto = g.scorta_minima != null
                                        && parseFloat(g.giacenza) < parseFloat(g.scorta_minima);
                                    return (
                                        <tr key={g.prodotto_id}
                                            className={g.anomalia ? 'mag-riga-allarme' : ''}>
                                            <td>
                                                {g.nome}
                                                <span className="mag-sub">
                                                    {g.codice}
                                                    {g.tracciabilita_rigorosa && ' · lotto obbligatorio'}
                                                </span>
                                            </td>
                                            <td className={`num ${sotto || g.anomalia ? 'mag-critico' : ''}`}>
                                                {num(g.giacenza)} {g.unita_misura}
                                                {sotto && <span className="mag-sub">min {num(g.scorta_minima)}</span>}
                                            </td>
                                            <td className="num">{g.lotti_aperti}</td>
                                            <td>{dataIta(g.prima_scadenza)}</td>
                                            <td className="num">{euro(g.valore)} €</td>
                                            <td className="num">
                                                <button className="mag-icona"
                                                        onClick={() => apriRettifica(g)}
                                                        disabled={!amministrazione && !(lottiPerArticolo[g.prodotto_id] || []).length}
                                                        title={amministrazione || (lottiPerArticolo[g.prodotto_id] || []).length
                                                            ? 'Rettifica la giacenza'
                                                            : 'Nessun lotto da rettificare'}>
                                                    <Pencil size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ---------- da scaricare ---------- */}
            {vista === 'scarico' && (
                <div className="mag-riquadro">
                    {codaScarico.length === 0 ? (
                        <p className="mag-vuoto">
                            Niente da scaricare: tutto il materiale usato nelle procedure
                            dell&apos;ultimo anno è già stato registrato.
                        </p>
                    ) : (
                        <>
                            <table className="mag-tabella rm-scorre">
                                <thead>
                                    <tr>
                                        <th />
                                        <th>Procedura</th>
                                        <th>Articolo</th>
                                        <th className="num">Q.tà</th>
                                        <th>Lotto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {codaScarico.map((r, i) => {
                                        const lotti = lottiPerArticolo[r.prodotto_id] || [];
                                        const senzaGiacenza = lotti.length === 0;
                                        return (
                                            <tr key={`${r.visita_prestazione_id}-${r.prodotto_id}`}
                                                className={senzaGiacenza && !r.tracciabilita_rigorosa
                                                    ? 'mag-riga-allarme' : ''}>
                                                <td>
                                                    {/* Sui tracciati la spunta non dipende dalla giacenza:
                                                        il lotto puo' essere di una confezione arrivata e
                                                        non ancora caricata, e la prestazione e' comunque
                                                        stata eseguita. */}
                                                    <input type="checkbox" checked={r.includi}
                                                           disabled={!r.tracciabilita_rigorosa && senzaGiacenza}
                                                           onChange={(e) => aggiornaCoda(i, 'includi', e.target.checked)} />
                                                </td>
                                                <td>
                                                    {r.cognome} {r.paziente_nome}
                                                    <span className="mag-sub">
                                                        {dataIta(r.data_visita)}
                                                        {r.medico_cognome ? ` · ${r.medico_titolo || ''} ${r.medico_cognome}` : ''}
                                                    </span>
                                                    {giorniDa(r.data_visita) > 30 && (
                                                        <span className="mag-sub mag-critico">
                                                            in attesa da {giorniDa(r.data_visita)} giorni
                                                        </span>
                                                    )}
                                                    {/* La prestazione e' stata eseguita PRIMA che questo
                                                        prodotto entrasse nella distinta: nessuno ha visto
                                                        la merce uscire per questa procedura, e il lotto
                                                        proposto oggi potrebbe essere arrivato dopo. Non e'
                                                        un errore - una distinta si puo' compilare dopo - ma
                                                        va deciso da chi sa cosa e' successo, non spuntato
                                                        da se'. */}
                                                    {r.retroattiva && (
                                                        <span className="mag-sub mag-critico">
                                                            la distinta e' stata scritta dopo questa
                                                            prestazione: controlla prima di scaricare
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {r.nome}
                                                    {r.tracciabilita_rigorosa && (
                                                        <span className="mag-sub mag-critico">
                                                            lotto da etichetta
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="num">
                                                    <input type="number" step="0.01" className="mag-qta"
                                                           value={r.quantita_scarico}
                                                           onChange={(e) => aggiornaCoda(i, 'quantita_scarico', e.target.value)} />
                                                </td>
                                                <td>
                                                    {r.tracciabilita_rigorosa ? (
                                                        /* Campo di testo e non tendina: il numero si legge
                                                           dal tagliando, e se la merce e' arrivata senza
                                                           essere caricata quel numero in tendina non c'e'.
                                                           Chi sceglie dalla lista sceglierebbe il primo
                                                           che gli somiglia. */
                                                        <>
                                                            <input className="mag-lotto"
                                                                   placeholder="numero di lotto"
                                                                   autoComplete="off" spellCheck="false"
                                                                   value={r.codice_lotto}
                                                                   onChange={(e) => aggiornaCoda(i, 'codice_lotto',
                                                                       e.target.value.toUpperCase())} />
                                                            {lotti.length > 0 && (
                                                                <span className="mag-sub">
                                                                    a magazzino: {lotti.map((l) => l.codice_lotto || '—').join(', ')}
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : senzaGiacenza ? (
                                                        <span className="mag-sub mag-critico">
                                                            nessun lotto disponibile
                                                        </span>
                                                    ) : (
                                                        <select value={r.lotto_id}
                                                                onChange={(e) => aggiornaCoda(i, 'lotto_id', e.target.value)}>
                                                            {lotti.map((l) => (
                                                                <option key={l.id} value={l.id}>
                                                                    {l.codice_lotto || 'senza lotto'}
                                                                    {l.scadenza ? ` · scad. ${dataIta(l.scadenza)}` : ''}
                                                                    {` · ${num(l.quantita_residua)} disp.`}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {daConfermare && (
                                <div className="mag-conferma">
                                    <p><AlertTriangle size={16} /> {daConfermare.messaggio}</p>
                                    <ul>
                                        {daConfermare.elenco.map((x, k) => (
                                            <li key={k}><b>{x.codice_lotto}</b> — {x.prodotto}</li>
                                        ))}
                                    </ul>
                                    <div className="mag-coda-azioni">
                                        <button className="mag-btn" onClick={() => setDaConfermare(null)}>
                                            Ricontrollo i numeri
                                        </button>
                                        <button className="mag-btn mag-btn--principale"
                                                onClick={() => scaricaSelezionate(true)} disabled={inCorso}>
                                            <Check size={16} /> Sono quelli sui tagliandi, registra
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mag-coda-azioni">
                                <p className="mag-nota">
                                    Sui prodotti tracciati il numero di lotto si scrive leggendolo
                                    dal tagliando che il medico consegna: quello proposto è solo il
                                    primo in scadenza a magazzino. Se il numero non risulta, viene
                                    chiesta conferma e il lotto nasce segnalato da regolarizzare —
                                    la prestazione è già stata eseguita, e non registrarla per una
                                    questione contabile perderebbe il dato clinico.
                                </p>
                                <button className="mag-btn mag-btn--principale"
                                        onClick={() => scaricaSelezionate(false)}
                                        disabled={inCorso || !pronte().length}>
                                    <Check size={16} /> Scarica {pronte().length} righe
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ---------- prodotti ---------- */}
            {vista === 'prodotti' && (
                <div className="mag-riquadro">
                    <div className="mag-ricerca">
                        <Search size={16} />
                        <input placeholder="Cerca prodotto…" value={ricerca}
                               onChange={(e) => setRicerca(e.target.value)} />
                    </div>
                    {prodotti.length === 0 ? (
                        <p className="mag-vuoto">
                            Nessun prodotto. Il primo si crea con «Nuovo prodotto»: un
                            prodotto è ciò che si consuma, non ciò che si vende.
                        </p>
                    ) : (
                        <table className="mag-tabella rm-scorre">
                            <thead>
                                <tr>
                                    <th>Prodotto</th>
                                    <th className="num">Giacenza</th>
                                    <th className="num">Scorta minima</th>
                                    <th className="num">Prestazioni</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {prodotti
                                    .filter((p) => !ricerca
                                        || (p.nome || '').toLowerCase().includes(ricerca.toLowerCase())
                                        || (p.codice || '').toLowerCase().includes(ricerca.toLowerCase()))
                                    .map((p) => (
                                    <tr key={p.prodotto_id}>
                                        <td>
                                            {p.nome}
                                            <span className="mag-sub">
                                                {p.codice}
                                                {p.tracciabilita_rigorosa && ' · lotto obbligatorio'}
                                            </span>
                                        </td>
                                        <td className={`num ${p.sotto_scorta ? 'mag-critico' : ''}`}>
                                            {num(p.giacenza)} {p.unita_misura}
                                        </td>
                                        <td className="num">
                                            {p.scorta_minima != null ? num(p.scorta_minima) : '—'}
                                        </td>
                                        <td className="num">{p.usato_da}</td>
                                        <td className="num">
                                            <button className="mag-icona" title="Modifica"
                                                    onClick={() => apriModuloProdotto({
                                                        id: p.prodotto_id, codice: p.codice || '',
                                                        nome: p.nome, unita_misura: p.unita_misura,
                                                        scorta_minima: p.scorta_minima ?? '',
                                                        tracciabilita_rigorosa: p.tracciabilita_rigorosa,
                                                        note: p.note || '',
                                                    })}>
                                                <Pencil size={16} />
                                            </button>
                                            <button className="mag-icona" title="Disattiva"
                                                    onClick={() => disattiva(p)}>
                                                <X size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <p className="mag-nota">
                        Un prodotto entra in gioco quando lo si collega a una prestazione,
                        dal catalogo: da lì si dice anche quanto ne consuma. La colonna
                        «Prestazioni» conta quante lo usano.
                    </p>
                </div>
            )}

            {/* ---------- scadenze ---------- */}
            {vista === 'scadenze' && (
                <div className="mag-riquadro">
                    {scadenze.length === 0 ? (
                        <p className="mag-vuoto">Nessun lotto in scadenza nei prossimi 90 giorni.</p>
                    ) : (
                        <table className="mag-tabella rm-scorre">
                            <tbody>
                                {scadenze.map((l) => (
                                    <tr key={l.id} className={l.giorni_alla_scadenza < 30 ? 'mag-riga-allarme' : ''}>
                                        <td>
                                            {l.nome}
                                            <span className="mag-sub">lotto {l.codice_lotto || '—'}</span>
                                        </td>
                                        <td className="num">{num(l.quantita_residua)}</td>
                                        <td>
                                            {dataIta(l.scadenza)}
                                            <span className="mag-sub">
                                                fra {l.giorni_alla_scadenza} giorni
                                            </span>
                                        </td>
                                        {/* Davanti a un lotto che scade la domanda
                                            successiva e' di chi erano le unita'
                                            rimaste: il collegamento va messo qui,
                                            dove uno si ferma a chiederselo. */}
                                        <td className="num">
                                            <button className="mag-btn"
                                                    onClick={() => {
                                                        setLottoPerConti(l.id);
                                                        setVista('conti');
                                                    }}>
                                                di chi sono
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ---------- conti medici ---------- */}
            {vista === 'conti' && (
                <ContiMedici key={lottoPerConti || 'tutti'} lottoIniziale={lottoPerConti} />
            )}

            {/* ---------- prestazioni senza distinta ---------- */}
            {vista === 'senza-distinta' && (
                <div className="mag-riquadro">
                    <p className="mag-nota">
                        Queste prestazioni <b>non dicono quale prodotto consumano</b>:
                        eseguirle non muove il magazzino, e su un prodotto a lotto
                        obbligatorio il numero di lotto non ha dove andare. Il
                        programma non può indovinare quali consumano davvero
                        qualcosa — <b>segnala quelle che nel nome nominano un
                        iniettabile</b>, e la decisione resta a chi conosce la
                        prestazione.
                    </p>

                    <div className="mag-filtri">
                        <input
                            type="text"
                            placeholder="Cerca per nome o codice…"
                            value={cercaPrestazione}
                            onChange={(e) => setCercaPrestazione(e.target.value)} />
                        <label className="mag-spunta">
                            <input type="checkbox" checked={soloIniettabili}
                                   onChange={(e) => setSoloIniettabili(e.target.checked)} />
                            <span>Solo quelle che nominano un iniettabile</span>
                        </label>
                    </div>

                    {(() => {
                        const q = cercaPrestazione.trim().toLowerCase();
                        const elenco = senzaDistinta
                            .filter((x) => (soloIniettabili ? x.nomina_iniettabile : true))
                            .filter((x) => !q
                                || String(x.nome || '').toLowerCase().includes(q)
                                || String(x.codice || '').toLowerCase().includes(q));
                        if (!elenco.length) {
                            return <p className="mag-vuoto">
                                {soloIniettabili
                                    ? 'Nessuna prestazione con un iniettabile nel nome è rimasta senza distinta.'
                                    : 'Nessuna prestazione senza distinta.'}
                            </p>;
                        }
                        return (
                            <>
                                <p className="mag-conteggio">
                                    {elenco.length} prestazioni
                                    {soloIniettabili ? ' con un iniettabile nel nome' : ''}
                                </p>
                                <table className="mag-tabella rm-scorre">
                                    <tbody>
                                        {elenco.map((x) => (
                                            <tr key={x.id}
                                                className={x.nomina_iniettabile ? 'mag-riga-allarme' : ''}>
                                                <td>
                                                    {x.nome}
                                                    <span className="mag-sub">
                                                        {x.codice}
                                                        {x.macro_area ? ` · ${x.macro_area}` : ''}
                                                        {x.volte_eseguita > 0
                                                            ? ` · eseguita ${x.volte_eseguita} volte`
                                                            : ' · mai eseguita'}
                                                    </span>
                                                </td>
                                                <td className="num">
                                                    <button className="mag-btn"
                                                            onClick={() => setDistintaDi(x)}>
                                                        Collega i prodotti
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        );
                    })()}
                </div>
            )}

            {distintaDi && (
                <DistintaPrestazione
                    prestazione={distintaDi}
                    onChiudi={() => setDistintaDi(null)}
                    onSalvata={(msg) => { setDistintaDi(null); setAvviso(msg); carica(); }} />
            )}

            {/* ---------- movimenti ---------- */}
            {vista === 'movimenti' && (
                <div className="mag-riquadro">
                    {movimenti.length === 0 ? (
                        <p className="mag-vuoto">Nessun movimento.</p>
                    ) : (
                        <table className="mag-tabella rm-scorre mag-tabella--movimenti">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Articolo</th>
                                    <th>Tipo</th>
                                    <th className="num">Quantità</th>
                                    <th>Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movimenti.map((m) => (
                                    <tr key={m.id}>
                                        <td>{dataIta(m.data_movimento)}</td>
                                        <td>
                                            {m.nome}
                                            <span className="mag-sub">
                                                {m.codice_lotto ? `lotto ${m.codice_lotto}` : ''}
                                                {m.paz_cognome ? ` · ${m.paz_cognome} ${m.paz_nome}` : ''}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`mag-tipo mag-tipo--${m.tipo}`}>{m.tipo}</span>
                                        </td>
                                        <td className={`num ${parseFloat(m.quantita) < 0 ? 'mag-uscita' : 'mag-entrata'}`}>
                                            {parseFloat(m.quantita) > 0 ? '+' : ''}{num(m.quantita)}
                                        </td>
                                        <td className="mag-sub">{m.motivo || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ---------- rettifica ---------- */}
            {formRettifica && (
                <div className="mag-modale-sfondo" onClick={chiudiRettifica}>
                    <div className="mag-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <span><Pencil size={18} /> Rettifica giacenza</span>
                            <button onClick={chiudiRettifica}><X size={18} /></button>
                        </div>
                        <div className="mag-modale-corpo">
                            <p className="mag-nota">
                                {formRettifica.articolo.nome} — giacenza attuale{' '}
                                <b>{num(formRettifica.articolo.giacenza)} {formRettifica.articolo.unita_misura}</b>
                            </p>

                            <label className="mag-campo">
                                <span>Lotto *</span>
                                <select value={formRettifica.lotto_id}
                                        onChange={(e) => setFormRettifica({
                                            ...formRettifica,
                                            lotto_id: e.target.value,
                                            costo: costoDelLotto(formRettifica.lotti, e.target.value),
                                            ...ivaDelLotto(formRettifica.lotti, e.target.value),
                                        })}>
                                    {formRettifica.lotti.map((l) => (
                                        <option key={l.id} value={l.id}>
                                            {l.codice_lotto || 'senza lotto'}
                                            {l.scadenza ? ` · scad. ${dataIta(l.scadenza)}` : ''}
                                            {` · ${num(l.quantita_residua)} disp.`}
                                            {l.costo_unitario != null ? ` · ${euro(l.costo_unitario)}` : ' · senza costo'}
                                            {l.stato && l.stato !== 'disponibile' ? ` · ${l.stato}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <div className="mag-campo">
                                <span>Imponibile unitario e aliquota IVA del lotto</span>
                                <div className="mag-costo-lotto">
                                    <input type="number" step="0.0001" min="0" placeholder="imponibile"
                                           value={formRettifica.imponibile}
                                           onChange={(e) => setFormRettifica({ ...formRettifica, imponibile: e.target.value })} />
                                    <input type="number" step="0.01" min="0" max="100" placeholder="IVA %" list="mag-aliquote"
                                           value={formRettifica.aliquota}
                                           onChange={(e) => setFormRettifica({ ...formRettifica, aliquota: e.target.value })} />
                                    {String(formRettifica.aliquota) === '0' && (
                                        <select value={formRettifica.natura}
                                                onChange={(e) => setFormRettifica({ ...formRettifica, natura: e.target.value })}>
                                            <option value="">natura</option>
                                            {NATURE_IVA.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    )}
                                    <button type="button" className="mag-btn" onClick={salvaCostoLotto}
                                            disabled={inCorso || !formRettifica.lotto_id
                                                      || formRettifica.imponibile === '' || formRettifica.aliquota === ''
                                                      || (String(formRettifica.aliquota) === '0' && !formRettifica.natura)}>
                                        Salva il costo
                                    </button>
                                </div>
                                <span className="mag-sub">
                                    {formRettifica.costo !== '' ? `Costo che vale oggi: ${euro(formRettifica.costo)} € — ${regolaCosto()}` : 'Senza costo'}
                                    {(formRettifica.lotti.find((l) => String(l.id) === String(formRettifica.lotto_id)) || {}).iva_da_verificare
                                        ? ' · IVA da verificare: il costo è stato scritto senza dire se l’IVA c’era' : ''}
                                </span>
                            </div>
                            {/* Dal 2027 il costo del lotto entra nei compensi dei
                                medici: correggerne uno che c'era chiede il motivo. */}
                            {costi.correzione_con_motivo
                                && costoDelLotto(formRettifica.lotti, formRettifica.lotto_id) !== '' && (
                                <label className="mag-campo">
                                    <span>Motivo della correzione del costo *</span>
                                    <input value={formRettifica.motivoCosto}
                                           placeholder="es. prezzo della fattura del fornitore"
                                           onChange={(e) => setFormRettifica({ ...formRettifica, motivoCosto: e.target.value })} />
                                </label>
                            )}
                            <p className="mag-nota">
                                Il costo si salva da solo, senza toccare la giacenza: per
                                correggere solo il prezzo non serve scrivere una differenza.
                            </p>

                            <label className="mag-campo">
                                <span>Tipo di rettifica *</span>
                                <select value={formRettifica.tipo}
                                        onChange={(e) => setFormRettifica({ ...formRettifica, tipo: e.target.value })}>
                                    <option value="rettifica">Conta di inventario</option>
                                    <option value="smaltimento">Smaltimento (rotto, scaduto)</option>
                                    {/* Il reso al fornitore e' una rettifica: merce che
                                        esce senza essere stata consumata, e che abbassa
                                        il tetto delle assegnazioni. «reso» nel magazzino
                                        e' lo storno di uno scarico, e lo scrive solo il
                                        gestionale. */}
                                    <option value="reso_fornitore">Reso al fornitore</option>
                                </select>
                            </label>

                            <label className="mag-campo">
                                <span>Differenza *</span>
                                <input type="number" step="0.01" placeholder="es. -2 oppure 3"
                                       value={formRettifica.differenza}
                                       onChange={(e) => setFormRettifica({ ...formRettifica, differenza: e.target.value })} />
                            </label>
                            <p className="mag-nota">
                                Quanto togliere o aggiungere, non il totale finale. Se ne contate
                                8 e il sistema ne dice 10, scrivete −2.
                                {formRettifica.differenza !== '' && !isNaN(parseFloat(formRettifica.differenza)) && (
                                    <> La giacenza diventerà{' '}
                                    <b>{num(parseFloat(formRettifica.articolo.giacenza)
                                        + parseFloat(formRettifica.differenza))}</b>.</>
                                )}
                            </p>

                            <label className="mag-campo">
                                <span>Motivo *</span>
                                <input placeholder="Cosa è successo"
                                       value={formRettifica.motivo}
                                       onChange={(e) => setFormRettifica({ ...formRettifica, motivo: e.target.value })} />
                            </label>
                            <div className="mag-motivi">
                                {['Conta di inventario', 'Confezione rotta', 'Scaduto e smaltito',
                                  'Errore di carico', 'Reso al fornitore'].map((m) => (
                                    <button key={m} type="button" className="mag-motivo"
                                            onClick={() => setFormRettifica({ ...formRettifica, motivo: m })}>
                                        {m}
                                    </button>
                                ))}
                            </div>

                            <p className="mag-nota">
                                La giacenza non viene sovrascritta: si registra il movimento che
                                la porta al valore giusto, e resta nello storico con il tuo nome.
                            </p>

                            <div className="mag-modale-azioni">
                                <button className="mag-btn" onClick={chiudiRettifica}>
                                    Annulla
                                </button>
                                <button className="mag-btn mag-btn--principale" onClick={salvaRettifica}
                                        disabled={inCorso || !formRettifica.lotto_id
                                                  || !formRettifica.motivo
                                                  || formRettifica.differenza === ''
                                                  || parseFloat(formRettifica.differenza) === 0}>
                                    Registra
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- prodotto: nuovo o modifica ---------- */}
            {pannello === 'prodotto' && formProdotto && (
                <div className="mag-modale-sfondo" onClick={chiudiProdotto}>
                    {/* «mag-modale-testata», come le altre tre finestre: fino al 22
                        settembre 2026 qui c'era «mag-modale-testa», che nessun CSS
                        conosce, e la X finiva sopra il titolo. */}
                    <div className="mag-modale mag-modale--prodotto" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <h2>{formProdotto.id ? 'Modifica prodotto' : 'Nuovo prodotto'}</h2>
                            <button onClick={chiudiProdotto}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="mag-modale-corpo">
                            <label className="mag-campo">
                                <span>Nome *</span>
                                <input value={formProdotto.nome} autoFocus
                                       placeholder="Acido ialuronico 1 ml"
                                       onChange={(e) => setFormProdotto({ ...formProdotto, nome: e.target.value })} />
                            </label>
                            <div className="mag-riga-campi">
                                <label className="mag-campo">
                                    <span>Codice</span>
                                    <input value={formProdotto.codice}
                                           onChange={(e) => setFormProdotto({ ...formProdotto, codice: e.target.value })} />
                                </label>
                                <label className="mag-campo">
                                    <span>Unità di misura</span>
                                    <input value={formProdotto.unita_misura}
                                           placeholder="fiala, ml, pz"
                                           onChange={(e) => setFormProdotto({ ...formProdotto, unita_misura: e.target.value })} />
                                </label>
                                <label className="mag-campo">
                                    <span>Scorta minima</span>
                                    <input type="number" step="0.01" value={formProdotto.scorta_minima}
                                           onChange={(e) => setFormProdotto({ ...formProdotto, scorta_minima: e.target.value })} />
                                </label>
                            </div>
                            {/* Casella ed etichetta insieme, cliccabili tutte e due
                                (label for); la spiegazione sotto, in riga propria. */}
                            <div className="mag-campo mag-tracciabilita">
                                <div className="mag-tracciabilita-riga">
                                    <input id="prodotto-tracciabilita" type="checkbox"
                                           checked={formProdotto.tracciabilita_rigorosa}
                                           onChange={(e) => setFormProdotto({ ...formProdotto, tracciabilita_rigorosa: e.target.checked })} />
                                    <label htmlFor="prodotto-tracciabilita">Tracciabilità rigorosa</label>
                                </div>
                                <p className="mag-tracciabilita-spiega">
                                    Il lotto va letto dall'etichetta: il sistema non lo sceglie
                                    da solo e la riga resta in «Da scaricare» finché qualcuno
                                    non lo indica. Serve su tossine, emoderivati, impiantabili.
                                </p>
                            </div>
                            <label className="mag-campo">
                                <span>Note</span>
                                <textarea rows="4" value={formProdotto.note}
                                          onChange={(e) => setFormProdotto({ ...formProdotto, note: e.target.value })} />
                            </label>

                            {/* ---- prestazioni che consumano questo prodotto ----
                                E' la distinta letta dal prodotto: la stessa tabella
                                che si compila dal modulo della prestazione. Da qui
                                si collega un prodotto a piu' prestazioni in un
                                gesto, invece di aprirle una per una. */}
                            <div className="mag-campo">
                                <span>Prestazioni che consumano questo prodotto</span>
                                {prestazioniProdotto === null ? (
                                    <p className="mag-nota">Lettura…</p>
                                ) : (
                                    <>
                                        {prestazioniProdotto.length > 0 && (
                                            <table className="mag-tabella rm-scorre mag-tabella--collegate">
                                                <tbody>
                                                    {prestazioniProdotto.map((x, i) => (
                                                        <tr key={x.prestazione_id}>
                                                            <td>{x.nome}</td>
                                                            <td className="num">
                                                                <input className="mag-qta" value={x.quantita}
                                                                       title="quanto se ne consuma per UNA prestazione"
                                                                       onChange={(e) => setPrestazioniProdotto(prestazioniProdotto.map((y, j) =>
                                                                           (j === i ? { ...y, quantita: e.target.value } : y)))} />
                                                                <span className="mag-sub"> {formProdotto.unita_misura}</span>
                                                            </td>
                                                            <td className="num">
                                                                <button className="mag-icona" title="Scollega"
                                                                        onClick={() => setPrestazioniProdotto(prestazioniProdotto.filter((_, j) => j !== i))}>
                                                                    <X size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                        <Select
                                            options={catalogoPrestazioni
                                                .filter((c) => !prestazioniProdotto.some((x) => x.prestazione_id === c.id))
                                                .map((c) => ({ value: c.id, label: c.nome }))}
                                            value={null}
                                            onChange={(o) => o && setPrestazioniProdotto([...prestazioniProdotto,
                                                { prestazione_id: o.value, nome: o.label, quantita: '1' }])}
                                            placeholder="Scrivi per cercare una prestazione da collegare…"
                                            noOptionsMessage={({ inputValue }) => (inputValue
                                                ? `Nessuna prestazione per «${inputValue}»` : 'Nessuna prestazione')}
                                            classNamePrefix="dist-select"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                        <small className="mag-sub">
                                            La quantità è quella di una prestazione: se la visita ne
                                            registra due, si scarica il doppio. Vale anche a rovescio,
                                            dal modulo della prestazione.
                                        </small>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="mag-modale-fondo">
                            <button className="mag-btn" onClick={chiudiProdotto}>
                                Annulla
                            </button>
                            <button className="mag-btn mag-btn--principale" onClick={salvaProdotto}
                                    disabled={!formProdotto.nome.trim() || inCorso}>
                                <Check size={16} /> Salva
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- carico manuale ---------- */}
            {pannello === 'carico' && (
                <div className="mag-modale-sfondo" onClick={chiudiCarico}>
                    <div className="mag-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <span><Plus size={18} /> Carico manuale</span>
                            <button onClick={chiudiCarico}><X size={18} /></button>
                        </div>
                        <div className="mag-modale-corpo">
                            <label className="mag-campo">
                                <span>Articolo *</span>
                                {/* Si scrive per cercare, come per i medici: con
                                    quattrocento articoli una tendina da scorrere
                                    e' una tendina in cui si sceglie quello sbagliato
                                    per stanchezza. Cerca su nome E codice.
                                    Il menu va nel body: dentro la finestra
                                    modale verrebbe tagliato dal suo overflow. */}
                                <Select
                                    options={prodotti.map((p) => ({
                                        value: String(p.prodotto_id),
                                        label: `${p.nome}${p.codice ? ` (${p.codice})` : ''}`,
                                    }))}
                                    value={formCarico.prodotto_id
                                        ? (() => {
                                            const p = prodotti.find((x) => String(x.prodotto_id) === String(formCarico.prodotto_id));
                                            return p ? { value: String(p.prodotto_id),
                                                         label: `${p.nome}${p.codice ? ` (${p.codice})` : ''}` } : null;
                                        })()
                                        : null}
                                    onChange={(o) => setFormCarico({ ...formCarico, prodotto_id: o ? o.value : '' })}
                                    placeholder="Scrivi per cercare per nome o codice…"
                                    noOptionsMessage={({ inputValue }) => (inputValue
                                        ? `Nessun articolo per «${inputValue}»`
                                        : 'Nessun articolo a magazzino')}
                                    isClearable
                                    autoFocus
                                    classNamePrefix="dist-select"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </label>
                            <div className="mag-riga-campi">
                                <label className="mag-campo">
                                    <span>Quantità *</span>
                                    <input type="number" step="0.01" value={formCarico.quantita}
                                           onChange={(e) => setFormCarico({ ...formCarico, quantita: e.target.value })} />
                                </label>
                                <label className="mag-campo">
                                    <span>{etichettaCosto(formCarico.prodotto_id)}</span>
                                    <input type="number" step="0.0001" min="0" value={formCarico.imponibile_unitario}
                                           onChange={(e) => setFormCarico({ ...formCarico, imponibile_unitario: e.target.value })} />
                                </label>
                            </div>
                            <div className="mag-riga-campi">
                                <label className="mag-campo">
                                    <span>Aliquota IVA (%)</span>
                                    <input type="number" step="0.01" min="0" max="100" list="mag-aliquote" value={formCarico.aliquota_iva}
                                           onChange={(e) => setFormCarico({ ...formCarico, aliquota_iva: e.target.value })} />
                                </label>
                                {String(formCarico.aliquota_iva) === '0' && (
                                    <label className="mag-campo">
                                        <span>Natura IVA *</span>
                                        <select value={formCarico.natura_iva}
                                                onChange={(e) => setFormCarico({ ...formCarico, natura_iva: e.target.value })}>
                                            <option value="">— dalla fattura —</option>
                                            {NATURE_IVA.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </label>
                                )}
                            </div>
                            <p className="mag-nota">Dalla fattura del fornitore: {regolaCosto()}.</p>
                            <div className="mag-riga-campi">
                                <label className="mag-campo">
                                    <span>Numero di lotto</span>
                                    <input value={formCarico.codice_lotto}
                                           onChange={(e) => setFormCarico({ ...formCarico, codice_lotto: e.target.value })} />
                                </label>
                                <label className="mag-campo">
                                    <span>Scadenza</span>
                                    <input type="date" value={formCarico.scadenza}
                                           onChange={(e) => setFormCarico({ ...formCarico, scadenza: e.target.value })} />
                                </label>
                            </div>
                            <div className="mag-riga-campi">
                                <label className="mag-campo">
                                    <span>Fornitore</span>
                                    <input value={formCarico.fornitore}
                                           onChange={(e) => setFormCarico({ ...formCarico, fornitore: e.target.value })} />
                                </label>
                                <label className="mag-campo">
                                    <span>Documento</span>
                                    <input value={formCarico.documento}
                                           onChange={(e) => setFormCarico({ ...formCarico, documento: e.target.value })} />
                                </label>
                            </div>
                            <p className="mag-nota">
                                Lotto e scadenza si leggono dalla confezione. Sugli articoli a
                                tracciabilità rigorosa il lotto è obbligatorio.
                            </p>
                            <div className="mag-modale-azioni">
                                <button className="mag-btn" onClick={chiudiCarico}>Annulla</button>
                                <button className="mag-btn mag-btn--principale" onClick={salvaCarico}
                                        disabled={!formCarico.prodotto_id || !formCarico.quantita || inCorso}>
                                    Carica
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- carico da fattura ---------- */}
            {pannello === 'xml' && (
                <div className="mag-modale-sfondo" onClick={() => { if (!xmlLetto) setPannello(null); }}>
                    <div className="mag-modale mag-modale--largo" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <span><Upload size={18} /> Carico da fattura o bolla</span>
                            <button onClick={() => { setPannello(null); setXmlLetto(null); setRigheXml([]); }}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="mag-modale-corpo">
                            {!xmlLetto ? (
                                <>
                                    {/* Prima di tutto quello che e' gia' in coda: le foto
                                        dal telefono aspettano qui una persona che le
                                        confermi. */}
                                    {lettureInAttesa.length > 0 && (
                                        <div className="mag-attesa">
                                            <b>Documenti letti, da confermare</b>
                                            <ul>
                                                {lettureInAttesa.map((l) => (
                                                    <li key={l.id}>
                                                        <button className="mag-link" disabled={l.stato === 'in_coda' || inCorso}
                                                                onClick={() => apriLettura(l.id)}>
                                                            {l.tipo === 'bolla' ? 'Bolla' : 'Fattura'}
                                                            {l.fornitore ? ` · ${l.fornitore}` : ''}
                                                            {l.righe ? ` · ${l.righe} righe` : ''}
                                                        </button>
                                                        <span className="mag-sub">
                                                            {dataIta(l.created_at)}
                                                            {l.chiesto_da_nome ? ` · ${l.chiesto_da_nome}` : ''}
                                                            {l.chiesto_da_ruolo === 'acquisizione' ? ' (dal telefono)' : ''}
                                                            {l.stato === 'in_coda' ? ' · in lettura…' : ''}
                                                            {l.stato === 'fallita' ? ` · non leggibile: ${l.errore || ''}` : ''}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <p className="mag-nota">
                                        Carica il file XML della fattura elettronica, oppure
                                        un PDF, una scansione o una fotografia della fattura o
                                        della bolla: il testo viene riconosciuto e letto, e
                                        <b> ogni riga va controllata</b> prima del carico.
                                        Dalla fattura escono quantità e prezzi; dalla bolla
                                        quantità, lotto e scadenza, che restano da verificare
                                        sulla confezione. Le foto scattate col telefono dello
                                        studio compaiono qui sopra.
                                    </p>
                                    <div className="mag-interruttore-tipo">
                                        <span>Un PDF o una foto caricati da qui sono:</span>
                                        <button className={tipoDocumento === 'fattura' ? 'attivo' : ''}
                                                onClick={() => setTipoDocumento('fattura')}>Fattura</button>
                                        <button className={tipoDocumento === 'bolla' ? 'attivo' : ''}
                                                onClick={() => setTipoDocumento('bolla')}>Bolla</button>
                                    </div>
                                    {letturaInCorso && (
                                        <p className="mag-nota mag-lettura-in-corso">
                                            Lettura in corso… su una scansione ci vogliono alcuni secondi.
                                        </p>
                                    )}
                                    <label
                                        className={`mag-rilascio ${sopra ? 'sopra' : ''}`}
                                        onDragOver={(e) => { e.preventDefault(); setSopra(true); }}
                                        onDragLeave={() => setSopra(false)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setSopra(false);
                                            const f = e.dataTransfer.files?.[0];
                                            /* Fattura elettronica o fattura da
                                               leggere: un file di altro tipo
                                               darebbe un errore incomprensibile
                                               piu' avanti. */
                                            if (!f) return;
                                            if (!/\.(xml|p7m|pdf|jpe?g|png|tiff?|heic|heif)$/i.test(f.name)) {
                                                notificaAvviso(
                                                    'Serve la fattura: XML o P7M della fattura '
                                                    + 'elettronica, oppure PDF, scansione o foto. '
                                                    + `Questo è "${f.name}".`
                                                );
                                                return;
                                            }
                                            leggiFile(f);
                                        }}
                                    >
                                        <Upload size={26} strokeWidth={1.5} />
                                        <b>Trascina qui il file della fattura</b>
                                        <span>oppure fai clic per sceglierlo — .xml, .p7m, .pdf o un'immagine</span>
                                        <input type="file" accept=".xml,.p7m,.pdf,image/*"
                                               onChange={(e) => leggiFile(e.target.files[0])} />
                                    </label>
                                </>
                            ) : (
                                <>
                                    <div className="mag-fattura-testata">
                                        <b>{xmlLetto.fornitore.nome || 'Fornitore non letto'}</b>
                                        <span>{xmlLetto.fornitore.piva ? `P.IVA ${xmlLetto.fornitore.piva}` : 'P.IVA non letta'}</span>
                                        <span>
                                            doc. {xmlLetto.documento.numero || '—'}
                                            {xmlLetto.documento.data ? ` del ${dataIta(xmlLetto.documento.data)}` : ''}
                                        </span>
                                        {/* Da una scansione i numeri sono letti, non
                                            trascritti: chi controlla deve saperlo. */}
                                        {xmlLetto.metodo && (
                                            <span className="mag-letto-da">
                                                {xmlLetto.tipo === 'bolla' ? 'BOLLA ' : ''}
                                                {xmlLetto.metodo === 'ocr'
                                                    ? `letta da scansione (confidenza ${xmlLetto.confidenza ?? '—'}%)`
                                                    : 'letta dal testo del PDF'}
                                                {xmlLetto.tipo === 'bolla'
                                                    ? ' — controlla quantità, lotto e scadenza sulle confezioni; qui non ci sono prezzi, quindi nessuna somma di controllo'
                                                    : ' — controlla quantità e prezzi riga per riga'}
                                            </span>
                                        )}
                                    </div>
                                    {/* La somma di controllo, in tre esiti: torna,
                                        non torna, non verificabile. I secondi due
                                        bloccano il carico finche' una persona non
                                        guarda e lo dichiara. */}
                                    {/* Anche con la somma che torna, una riga puo'
                                        non tornare da sola - un 6 letto come 8 con
                                        l'importo giusto - e ferma il carico allo
                                        stesso modo. */}
                                    {xmlLetto.quadratura && xmlLetto.quadratura.torna
                                      && righeXml.some((x) => x.includi && x.incoerente) && (
                                        <div className="mag-errore mag-quadratura">
                                            <AlertTriangle size={16} />
                                            <div>
                                                <b>La somma di controllo torna, ma {righeXml.filter((x) => x.includi && x.incoerente).length === 1
                                                    ? 'una riga non torna' : `${righeXml.filter((x) => x.includi && x.incoerente).length} righe non tornano`}</b>:
                                                quantità per prezzo unitario non dà l&apos;importo di riga. Una quantità
                                                letta male, o uno sconto di riga: guarda la fattura.
                                                <label className="mag-spunta">
                                                    <input type="checkbox" checked={controllato}
                                                           onChange={(e) => setControllato(e.target.checked)} />
                                                    Ho controllato le righe
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    {xmlLetto.quadratura && xmlLetto.quadratura.torna
                                      && !righeXml.some((x) => x.includi && x.incoerente) && (
                                        <div className="mag-quadratura mag-quadratura--ok">
                                            Somma di controllo: righe {euro(xmlLetto.quadratura.righe)}
                                            {Number(xmlLetto.quadratura.altri) !== 0 && ` + altre voci ${euro(xmlLetto.quadratura.altri)}`}
                                            {' = imponibile '}{euro(xmlLetto.quadratura.imponibile)}: torna.
                                        </div>
                                    )}
                                    {xmlLetto.quadratura && !xmlLetto.quadratura.torna && (
                                        <div className="mag-errore mag-quadratura">
                                            <AlertTriangle size={16} />
                                            <div>
                                                {xmlLetto.quadratura.verificabile ? (
                                                    <>
                                                        <b>La somma di controllo non torna.</b> Righe {euro(xmlLetto.quadratura.righe)}
                                                        {Number(xmlLetto.quadratura.altri) !== 0 && ` + altre voci ${euro(xmlLetto.quadratura.altri)}`}
                                                        {' contro imponibile '}{euro(xmlLetto.quadratura.imponibile)}
                                                        {': differenza '}{euro(xmlLetto.quadratura.differenza)}.
                                                        {xmlLetto.quadratura.righe_senza_importo > 0
                                                            && ` ${xmlLetto.quadratura.righe_senza_importo} righe senza importo.`}
                                                        {' Una riga persa o un numero letto male: guarda la fattura riga per riga.'}
                                                    </>
                                                ) : (
                                                    <><b>Somma di controllo non possibile.</b> {xmlLetto.quadratura.motivo}</>
                                                )}
                                                <label className="mag-spunta">
                                                    <input type="checkbox" checked={controllato}
                                                           onChange={(e) => setControllato(e.target.checked)} />
                                                    Ho controllato le righe
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    {xmlLetto.gia_importato && (
                                        <div className="mag-errore">
                                            <AlertTriangle size={16} />
                                            Questo documento risulta già importato il{' '}
                                            {dataIta(xmlLetto.gia_importato.created_at)}.
                                        </div>
                                    )}
                                    <table className="mag-tabella rm-scorre mag-tabella--xml">
                                        <thead>
                                            <tr>
                                                <th />
                                                <th>Riga della fattura</th>
                                                <th>Prodotto di magazzino</th>
                                                <th className="num">Q.tà</th>
                                                <th>Lotto</th>
                                                <th>Scadenza</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {righeXml.map((r, i) => (
                                                <tr key={i} className={!r.scelta ? 'mag-riga-allarme' : ''}>
                                                    <td>
                                                        <input type="checkbox" checked={r.includi}
                                                               onChange={(e) => aggiornaRiga(i, 'includi', e.target.checked)} />
                                                    </td>
                                                    <td>
                                                        {r.descrizione}
                                                        <span className="mag-sub">
                                                            {r.codice_principale} · {euro(r.costo_effettivo)} € + IVA {r.aliquota != null ? `${r.aliquota}%` : '?'}{r.natura ? ` ${r.natura}` : ''}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <select value={r.scelta}
                                                                onChange={(e) => aggiornaRiga(i, 'scelta', e.target.value)}>
                                                            <option value="">— non caricare —</option>
                                                            {r.abbinamento && (
                                                                <option value={r.abbinamento.prodotto_id}>
                                                                    {r.abbinamento.nome} (memorizzato)
                                                                </option>
                                                            )}
                                                            {r.proposte.map((p) => (
                                                                <option key={p.id} value={p.id}>{p.nome}</option>
                                                            ))}
                                                            {prodotti.map((p) => (
                                                                <option key={`c${p.prodotto_id}`}
                                                                        value={p.prodotto_id}>{p.nome}</option>
                                                            ))}
                                                        </select>
                                                        {r.abbinamento && (
                                                            <span className="mag-origine mag-origine--memoria">
                                                                gia' abbinato in passato
                                                            </span>
                                                        )}
                                                        {!r.abbinamento && r.assistenza && (
                                                            <span className="mag-origine mag-origine--assistenza"
                                                                  title={r.assistenza.motivo || ''}>
                                                                proposto dall'assistenza
                                                                {r.assistenza.sicurezza === 'bassa' && ' · da verificare'}
                                                                {r.assistenza.motivo && ` — ${r.assistenza.motivo}`}
                                                            </span>
                                                        )}
                                                        {/* Due cose diverse, e prima erano scritte uguali:
                                                            «il server propone questo» e «ci sono dei
                                                            candidati ma nessuno somiglia abbastanza». La
                                                            seconda e' un invito a guardare, non una
                                                            proposta. */}
                                                        {!r.abbinamento && !r.assistenza
                                                          && r.origine_abbinamento === 'proposta' && (
                                                            <span className="mag-origine">
                                                                proposto per somiglianza del nome
                                                            </span>
                                                        )}
                                                        {!r.abbinamento && !r.assistenza
                                                          && r.origine_abbinamento !== 'proposta'
                                                          && r.proposte.length > 0 && (
                                                            <span className="mag-origine mag-origine-debole">
                                                                nessuno somiglia abbastanza: {r.proposte.length}
                                                                {r.proposte.length === 1 ? ' candidato da guardare' : ' candidati da guardare'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="num">
                                                        {/* Dall'XML la quantita' e' un dato del
                                                            tracciato e non si tocca. Da un PDF e'
                                                            LETTA - da una scansione puo' mancare o
                                                            essere sbagliata - e si corregge qui. */}
                                                        {r.incoerente && (
                                                            <span className="mag-origine mag-origine-debole" title={r.incoerente}>
                                                                non torna: {r.incoerente}
                                                            </span>
                                                        )}
                                                        {xmlLetto.metodo ? (
                                                            <input type="number" step="0.01" min="0"
                                                                   className={`mag-qta ${!(parseFloat(r.quantita) > 0) || r.incoerente ? 'mag-qta--manca' : ''}`}
                                                                   value={r.quantita ?? ''}
                                                                   placeholder="?"
                                                                   onChange={(e) => aggiornaRiga(i, 'quantita', e.target.value)} />
                                                        ) : num(r.quantita)}
                                                    </td>
                                                    <td>
                                                        <input value={r.codice_lotto} placeholder="dalla scatola"
                                                               className={r.lotto_letto ? 'mag-qta--manca' : ''}
                                                               title={r.lotto_letto ? 'letto dalla bolla: verificalo sulla confezione' : ''}
                                                               onChange={(e) => aggiornaRiga(i, 'codice_lotto', e.target.value)} />
                                                    </td>
                                                    <td>
                                                        <input type="date" value={r.scadenza}
                                                               className={r.lotto_letto ? 'mag-qta--manca' : ''}
                                                               title={r.lotto_letto ? 'letta dalla bolla: verificala sulla confezione' : ''}
                                                               onChange={(e) => aggiornaRiga(i, 'scadenza', e.target.value)} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <p className="mag-nota">
                                        Gli abbinamenti confermati vengono memorizzati: la prossima
                                        fattura di questo fornitore sarà già compilata.
                                    </p>
                                    <div className="mag-modale-azioni">
                                        <button className="mag-btn"
                                                onClick={() => { setXmlLetto(null); setRigheXml([]); }}>
                                            Indietro
                                        </button>
                                        <button className="mag-btn mag-btn--principale"
                                                onClick={confermaXml} disabled={inCorso}>
                                            <Check size={16} /> Carica{' '}
                                            {righeXml.filter((r) => r.includi && r.scelta).length} righe
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Magazzino;
